class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.syncCartLinkCounts();
    this.cartCountObserver = new MutationObserver(() => this.syncCartLinkCounts());
    this.cartCountObserver.observe(this.querySelector('#CartDrawer'), { childList: true, subtree: true });
    // Restore the closed state when returning from another page's browser history.
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.close(); });
  }

  setHeaderCartIconAccessibility() {
    this.cartLinks = Array.from(document.querySelectorAll('#cart-icon-bubble, #bhp-home-alt .ha-nav-tools a[href="/cart"]'));
    this.cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
      cartLink.setAttribute('aria-expanded', 'false');
      cartLink.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  syncCartLinkCounts() {
    const count = this.querySelector('.bhp-cart-count');
    if (!count) return;
    const itemCount = Number(count.textContent.trim());
    if (!Number.isFinite(itemCount)) return;
    this.cartLinks.filter((link) => link.id !== 'cart-icon-bubble').forEach((link) => {
      link.setAttribute('aria-label', `Cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`);
      let badge = link.querySelector('span:not(.ha-tool-label)');
      if (!badge && itemCount) {
        badge = document.createElement('span');
        link.appendChild(badge);
      }
      if (badge) { badge.textContent = itemCount ? String(itemCount) : ''; badge.hidden = itemCount === 0; }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    else if (!this.classList.contains('active') && !this.contains(document.activeElement)) this.setActiveElement(document.activeElement);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
      this.cartLinks.forEach((link) => link.setAttribute('aria-expanded', 'true'));
      // Focus does not depend on a transition, including reduced-motion settings.
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => {
        if (this.classList.contains('active')) trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
      }, 80);
    });
    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.openTimer);
    clearTimeout(this.focusTimer);
    this.classList.remove('active');
    this.cartLinks.forEach((link) => link.setAttribute('aria-expanded', 'false'));
    removeTrapFocus(this.activeElement && this.activeElement.isConnected ? this.activeElement : this.cartLinks.find((link) => link.offsetParent));
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
